/**
 * ApiService - Базовый класс для API запросов
 * ============================================
 * 
 * Стандартизирует все API вызовы, обработку ошибок и логирование
 */

import { IS_DEBUG, describePayload } from '../utils/debug';

// В production используем относительный путь (тот же домен)
// В development используем localhost:4600
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? '/api'  // Относительный путь для production
  : (process.env.REACT_APP_API_URL || 'http://localhost:4600/api');

/**
 * Отмена запроса — ожидаемый control flow, а не ошибка
 * @param {*} error - Пойманная ошибка
 * @returns {boolean}
 */
export function isAbortError(error) {
  return error?.name === 'AbortError';
}

export class ApiService {
  /**
   * Выполняет HTTP запрос
   * @param {string} endpoint - Путь относительно API_BASE_URL
   * @param {Object} options - fetch-опции; поддерживается signal (AbortController)
   */
  static async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;

    try {
      // options может содержать signal — он уходит в fetch как есть
      const response = await fetch(url, {
        ...options,
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      });

      // Проверяем HTTP статус
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      // Парсим JSON
      const data = await response.json();

      // Проверяем успешность на уровне API
      if (data.success === false) {
        throw new Error(data.error || 'Request failed');
      }

      const result = data.data || data;

      // Логируем только форму ответа, не payload (горячий путь)
      if (IS_DEBUG) {
        console.log(`API [${endpoint}] →`, describePayload(result));
      }

      return result;
    } catch (error) {
      // Прерванный запрос не считаем ошибкой и не шумим в консоль
      if (isAbortError(error)) {
        throw error;
      }
      console.error(`API Error [${endpoint}]:`, error?.message || error);
      throw error;
    }
  }

  /**
   * GET запрос
   * @param {string} endpoint - Путь относительно API_BASE_URL
   * @param {Object} params - Query-параметры (массивы разворачиваются в повторяющиеся ключи)
   * @param {Object} options - Доп. fetch-опции, в первую очередь { signal }
   */
  static async get(endpoint, params = {}, options = {}) {
    const url = this.buildUrl(endpoint, params);

    return this.request(url, {
      ...options,
      method: 'GET'
    });
  }

  /**
   * Убирает пустые параметры
   * Единственная реализация нормализации: её же использует ключ клиентского
   * кэша (MetricsService.cachedGet), иначе одинаковые URL давали бы разные ключи
   * @param {Object} params - Сырые query-параметры
   * @returns {Object} - Параметры без undefined/null/''
   */
  static normalizeParams(params = {}) {
    return Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
  }

  /**
   * Собирает URL с query string из нормализованных параметров
   * @param {string} endpoint - Путь относительно API_BASE_URL
   * @param {Object} params - Сырые query-параметры
   * @returns {string} - endpoint либо endpoint?query
   */
  static buildUrl(endpoint, params = {}) {
    // Правильно обрабатываем массивы для query string
    const queryString = this.buildQueryString(this.normalizeParams(params));
    return queryString ? `${endpoint}?${queryString}` : endpoint;
  }

  /**
   * Строит query string с правильной обработкой массивов
   */
  static buildQueryString(params) {
    const parts = [];
    
    for (const [key, value] of Object.entries(params)) {
      if (Array.isArray(value)) {
        // Для массивов добавляем каждый элемент отдельно
        value.forEach(item => {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`);
        });
      } else {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      }
    }
    
    return parts.join('&');
  }
  
  /**
   * POST запрос
   * @param {string} endpoint - Путь относительно API_BASE_URL
   * @param {Object} body - Тело запроса
   * @param {Object} options - Доп. fetch-опции, в первую очередь { signal }
   */
  static async post(endpoint, body, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body)
    });
  }
  
  /**
   * PUT запрос
   */
  static async put(endpoint, body) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body)
    });
  }
  
  /**
   * DELETE запрос
   */
  static async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE'
    });
  }
  
  /**
   * Batch запрос для получения нескольких данных
   */
  static async batch(requests) {
    const promises = requests.map(({ method = 'GET', endpoint, params, body }) => {
      switch (method) {
        case 'POST':
          return this.post(endpoint, body);
        case 'PUT':
          return this.put(endpoint, body);
        case 'DELETE':
          return this.delete(endpoint);
        default:
          return this.get(endpoint, params);
      }
    });
    
    return Promise.all(promises);
  }
}

// Экспортируем удобные функции
export const apiGet = (endpoint, params, options) => ApiService.get(endpoint, params, options);
export const apiPost = (endpoint, body, options) => ApiService.post(endpoint, body, options);
export const apiPut = (endpoint, body) => ApiService.put(endpoint, body);
export const apiDelete = (endpoint) => ApiService.delete(endpoint);
